import type { LinkStatus } from "../../../../store/public/publicTrackingLinkStore";

interface LinkStatusScreenProps {
  status: LinkStatus;
  errorMessage?: string | null;
}

export function LinkStatusScreen({ status, errorMessage }: LinkStatusScreenProps) {
  const statusConfig = {
    loading: {
      icon: "ri-loader-4-line",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      title: "Cargando...",
      description: "Estamos verificando el link de seguimiento.",
      animate: true,
    },
    not_found: {
      icon: "ri-error-warning-line",
      iconBg: "bg-red-100",
      iconColor: "text-red-600",
      title: "Link No Encontrado",
      description: "El link de seguimiento que buscas no existe o ha sido eliminado.",
      animate: false,
    },
    expired: {
      icon: "ri-time-line",
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
      title: "Link Expirado",
      description: "Este link de seguimiento ha expirado y ya no está disponible.",
      animate: false,
    },
    inactive: {
      icon: "ri-stop-circle-line",
      iconBg: "bg-gray-100",
      iconColor: "text-gray-600",
      title: "Link Desactivado",
      description: "Este link de seguimiento ha sido desactivado por el administrador.",
      animate: false,
    },
    pending: {
      icon: "ri-calendar-schedule-line",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      title: "Aún No Activo",
      description: "Este link de seguimiento aún no está activo. Vuelve más tarde.",
      animate: false,
    },
    error: {
      icon: "ri-signal-wifi-error-line",
      iconBg: "bg-red-100",
      iconColor: "text-red-600",
      title: "Error de Conexión",
      description: errorMessage || "No pudimos conectar con el servidor. Intenta de nuevo más tarde.",
      animate: false,
    },
  };

  const config = statusConfig[status as keyof typeof statusConfig];

  if (!config) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
        {/* Icon */}
        <div
          className={`w-20 h-20 ${config.iconBg} rounded-full flex items-center justify-center mx-auto mb-6`}
        >
          <i
            className={`${config.icon} text-4xl ${config.iconColor} ${
              config.animate ? "animate-spin" : ""
            }`}
          ></i>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-3">{config.title}</h1>

        {/* Description */}
        <p className="text-gray-500 mb-6">{config.description}</p>

        {/* Actions */}
        {status === "error" && (
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-all flex items-center justify-center gap-2 mx-auto"
          >
            <i className="ri-refresh-line"></i>
            <span>Reintentar</span>
          </button>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-400">
            Si crees que esto es un error, contacta a la persona que te compartió
            este link.
          </p>
        </div>
      </div>
    </div>
  );
}
